import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plane, Wallet, Home, Bell, BookOpen, UtensilsCrossed, Package, Newspaper,
  Volume2, Square, MapPin, HeartPulse, Users, CalendarHeart,
  Flower2, TreePine, Sun, ListChecks, AlarmClockPlus, Receipt, ExternalLink, Vault, Mic, Settings2,
  ChefHat, Loader2,
} from "lucide-react";
import api from "../lib/api";
import Weather from "../components/Weather";
import { useAuth } from "../context/AuthContext";
import { FeedGrid, FeedCard, InterestChips, PremiumUpsell } from "../components/pulse/FeedCards";

/* My Social Pulse — personal life-hub shell (Today / My Life / Family / News).
   Content cards come from the SocialPulse Content & Recommendation Service;
   private lists and diaries stay in their own services. */

const TILES = [
  { icon: Plane, to: "/travel-hub", tone: "bg-blue-700", title: "Travel Hub", text: "Trips, itineraries & documents" },
  { icon: Wallet, to: "/travel-hub#hub-expenses", tone: "bg-teal-600", title: "Trip Expenses", text: "Budgets, receipts & settle up" },
  { icon: Home, to: "/expenses", tone: "bg-orange-500", title: "Home Expenses", text: "Bills, groceries & recurring" },
  { icon: Bell, to: "/remind-me", tone: "bg-rose-600", title: "Reminders", text: "Never miss a date or a bill" },
  { icon: BookOpen, to: "/travel-diaries", tone: "bg-violet-600", title: "Travel Diary", text: "Your journey, day by day" },
  { icon: UtensilsCrossed, to: "/my-social-pulse/food", tone: "bg-emerald-600", title: "Food & Kitchen", text: "Diary, recipes, deals & shopping" },
  { icon: Package, to: "/my-social-pulse/pantry", tone: "bg-amber-600", title: "Home Inventory", text: "What's in stock, what's expiring" },
  { icon: Vault, to: "/locker", tone: "bg-zinc-800", title: "Documents Locker", text: "License, insurance & ID copies — PIN protected" },
  { icon: Newspaper, to: "/my-social-pulse?tab=news", tone: "bg-sky-600", title: "News & Listening", text: "Headlines you can listen to" },
];

const TABS = [["today", "Today", Sun], ["travel", "My Travel", Plane], ["life", "My Life", ListChecks], ["family", "Family", Users], ["news", "News & Listening", Newspaper]];

/* Per-device voice settings for briefings & news (voices differ per device). */
export const getTtsPrefs = () => { try { return JSON.parse(localStorage.getItem("tp_tts") || "{}"); } catch { return {}; } };
export const applyTtsPrefs = (u) => {
  const p = getTtsPrefs();
  u.rate = p.rate || 0.95;
  if (p.voiceURI) {
    const v = window.speechSynthesis?.getVoices().find((x) => x.voiceURI === p.voiceURI);
    if (v) u.voice = v;
  }
};

export default function MySocialPulse() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([id]) => id === params.get("tab")) ? params.get("tab") : "today";
  const first = (user?.name || "there").split(" ")[0];

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="my-social-pulse">
      <div className="text-xs font-mono uppercase tracking-widest text-blue-700 flex items-center gap-1.5"><HeartPulse className="w-4 h-4" /> My Social Pulse</div>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight mt-1" data-testid="msp-greeting">Hello, {first}</h1>
      <p className="text-zinc-600 mt-1">Your network, made personal — private unless you choose to share.</p>

      <div className="mt-5 hidden md:flex gap-2 flex-wrap sticky top-0 z-10 bg-zinc-50/95 backdrop-blur py-2 -mx-1 px-1">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setParams(id === "today" ? {} : { tab: id })} data-testid={`msp-tab-${id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-700 border transition-colors ${tab === id ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-blue-600"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "today" && <TodayTab />}
      {tab === "travel" && <MyTravelTab />}
      {tab === "life" && <MyLifeTab />}
      {tab === "family" && <FamilyTab />}
      {tab === "news" && <NewsGate />}
    </div>
  );
}

/* News & Listening — free. Sub-tabs: headlines + sister-site feeds. */
function NewsGate() {
  const [sub, setSub] = useState("headlines");
  const SUBS = [
    ["headlines", "Headlines"], ["naturepulse", "Nature"],
    ["alumnipulse", "Alumni"], ["indianfamilytree", "Family Tree"],
  ];
  return (
    <div className="mt-4" data-testid="msp-news-section">
      <div className="flex gap-2 flex-wrap">
        {SUBS.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`news-sub-${id}`}
            className={`px-3.5 py-2 rounded-full text-sm font-700 border transition-colors ${sub === id ? "bg-sky-700 text-white border-sky-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-sky-600"}`}>
            {label}
          </button>
        ))}
      </div>
      {sub === "headlines" ? <DailyNews /> : (
        <div className="mt-4">
          <FeedGrid source={sub} emptyText="No stories from this source yet — check back soon." />
        </div>
      )}
    </div>
  );
}

/* ---------------- My Travel: trip-attached suggestions ---------------- */
function MyTravelTab() {
  const [trips, setTrips] = useState(null);
  const [tid, setTid] = useState("");
  const [feed, setFeed] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/trips").then((r) => {
      const items = r.data.items || [];
      setTrips(items);
      if (items.length > 0) setTid(items[0].id);
    }).catch(() => setTrips([]));
  }, []);

  useEffect(() => {
    if (!tid) return;
    setFeed(null);
    api.get(`/pulse/trip-feed/${tid}`).then((r) => { setFeed(r.data); setErr(false); })
      .catch((e) => { setErr(e.response?.status === 402 ? "premium" : true); setFeed({ items: [] }); });
  }, [tid]);

  const days = useMemo(() => {
    const t = feed?.trip;
    if (!t?.start_date || !t?.end_date) return 1;
    return Math.max(1, Math.round((new Date(t.end_date) - new Date(t.start_date)) / 86400000) + 1);
  }, [feed]);

  const onChanged = (action, it) => {
    if (action === "hide") setFeed((f) => ({ ...f, items: f.items.filter((x) => x.gid !== it.gid) }));
    if (action === "save-to-trip" || action === "unsave-from-trip")
      setFeed((f) => ({ ...f, items: f.items.map((x) => x.gid === it.gid ? { ...x, saved_to_trip: action === "save-to-trip" } : x) }));
  };

  if (!trips) return <p className="mt-6 text-sm text-zinc-500">Loading your trips…</p>;
  if (trips.length === 0) return (
    <div className="mt-6 text-center border border-dashed border-zinc-300 rounded-2xl py-8" data-testid="msp-travel-empty">
      <Plane className="w-7 h-7 text-blue-700 mx-auto" />
      <p className="text-sm text-zinc-600 mt-2">No trips yet — plan one first, then suggestions appear here.</p>
      <Link to="/travel-hub" className="inline-block mt-3 bg-blue-700 text-white text-sm font-700 px-5 py-2.5 rounded-full hover:bg-blue-800" data-testid="msp-travel-plan-cta">Open Travel Hub</Link>
    </div>
  );

  return (
    <div className="mt-4" data-testid="msp-travel">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-sm font-700 text-zinc-700" htmlFor="msp-trip-select">For your journey:</label>
        <select id="msp-trip-select" value={tid} onChange={(e) => setTid(e.target.value)} data-testid="msp-trip-select"
          className="notranslate border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[200px]">
          {trips.map((t) => <option key={t.id} value={t.id}>{t.title}{t.start_date ? ` — ${t.start_date}` : ""}</option>)}
        </select>
        {tid && <Link to={`/trips/${tid}`} className="text-xs font-700 text-blue-700 hover:underline" data-testid="msp-trip-open">Open trip →</Link>}
      </div>
      <p className="text-xs text-zinc-500 mt-2">Editorial suggestions only — never bookings or flight alerts. "Add to plan" creates an editable proposal on your day plan.</p>

      {err === "premium" ? <div className="mt-4"><PremiumUpsell feature="Trip suggestions" /></div>
        : !feed ? <p className="mt-5 text-sm text-zinc-500">Finding suggestions…</p>
        : feed.items.length === 0 ? <p className="mt-5 text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-xl" data-testid="msp-travel-feed-empty">No suggestions for this trip right now.</p>
        : (
          <>
            {feed.matched_count > 0 && <p className="mt-4 text-xs font-700 text-violet-700" data-testid="msp-travel-matched">{feed.matched_count} suggestion{feed.matched_count > 1 ? "s" : ""} matched to this trip's destinations</p>}
            <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="msp-travel-feed">
              {feed.items.map((it) => <FeedCard key={it.gid} item={it} trip={{ id: tid, days }} onChanged={onChanged} />)}
            </div>
          </>
        )}
    </div>
  );
}

/* ---------------- Today ---------------- */
function TodayTab() {
  const [reminders, setReminders] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [voicePanel, setVoicePanel] = useState(false);
  const [nextTrip, setNextTrip] = useState(null);
  const loc = useMemo(() => { try { return JSON.parse(localStorage.getItem("aa_location") || "null"); } catch { return null; } }, []);

  useEffect(() => {
    api.get("/reminders", { params: { view: "today" } })
      .then((r) => setReminders(r.data.items || [])).catch(() => setReminders([]));
    api.get("/trips").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const up = (r.data.items || []).filter((t) => t.start_date && t.start_date >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date));
      setNextTrip(up[0] || null);
    }).catch(() => {});
    return () => window.speechSynthesis?.cancel();
  }, []);

  const daysToTrip = nextTrip ? Math.max(0, Math.round((new Date(nextTrip.start_date) - new Date(new Date().toISOString().slice(0, 10))) / 864e5)) : null;

  const briefing = async () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (speaking) { synth.cancel(); setSpeaking(false); return; }
    let text = `Good ${new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}. `;
    if (reminders?.length) text += `You have ${reminders.length} reminder${reminders.length > 1 ? "s" : ""} today: ${reminders.slice(0, 5).map((r) => r.title).join(". ")}. `;
    else text += "No reminders due today. ";
    try {
      const r = await api.get("/pulse/feed", { params: { module: "today" } });
      const t = (r.data.items || []).filter((i) => i.rights?.summary_tts).slice(0, 3);
      if (t.length) text += `For you today: ${t.map((i) => `${i.title}, from ${i.source_name}`).join(". ")}.`;
    } catch { /* briefing works without the feed */ }
    const u = new SpeechSynthesisUtterance(text);
    applyTtsPrefs(u);
    u.onend = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(u);
  };

  const QUICK = [
    { icon: Receipt, label: "Add expense", to: "/expenses", tone: "bg-teal-600" },
    { icon: ListChecks, label: "Make a list", to: "/my-social-pulse/food?tab=shopping", tone: "bg-violet-600" },
    { icon: AlarmClockPlus, label: "Set reminder", to: "/remind-me", tone: "bg-orange-500" },
    { icon: Plane, label: "Plan a trip", to: "/travel-hub", tone: "bg-blue-700" },
  ];

  const markDone = async (r) => {
    try {
      await api.post(`/reminders/${r.id}/done`);
      setReminders((arr) => arr.filter((x) => x.id !== r.id));
    } catch { /* leave it visible on failure */ }
  };

  return (
    <div className="mt-4" data-testid="msp-today">
      {nextTrip && daysToTrip !== null && (
        <Link to={`/trips/${nextTrip.id}`} data-testid="msp-trip-countdown"
          className="mb-3 flex items-center gap-3 bg-blue-700 text-white rounded-2xl px-4 py-3.5 hover:bg-blue-800 transition-colors">
          <Plane className="w-6 h-6 -rotate-45 shrink-0" />
          <span className="font-heading font-800 text-lg leading-tight">
            {daysToTrip === 0 ? "Today's the day" : `${daysToTrip} day${daysToTrip > 1 ? "s" : ""} to go`} — {nextTrip.title}
          </span>
          <span className="ml-auto text-xs opacity-80 shrink-0">{nextTrip.start_date}</span>
        </Link>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-sky-50 border border-sky-200 rounded-2xl px-4 py-3.5" data-testid="msp-weather">
          {loc?.lat != null ? (
            <>
              <div className="text-[11px] font-mono uppercase text-sky-700 flex items-center gap-1"><MapPin className="w-3 h-3" /> {loc.city || "Your area"} today</div>
              <Weather lat={loc.lat} lon={loc.lon} compact />
            </>
          ) : (
            <div className="text-sm text-zinc-600" data-testid="msp-weather-empty"><MapPin className="w-4 h-4 inline mr-1 text-sky-600" />Choose your location in the top bar to see daily weather. Location is manual — never tracked.</div>
          )}
        </div>
        <div className="relative">
          <button onClick={briefing} data-testid="msp-briefing-btn"
            className={`w-full h-full rounded-2xl px-4 py-3.5 font-700 text-left flex items-center gap-3 transition-colors ${speaking ? "bg-rose-600 text-white" : "bg-blue-700 text-white hover:bg-blue-800"}`}>
            {speaking ? <Square className="w-5 h-5 shrink-0" /> : <Volume2 className="w-6 h-6 shrink-0" />}
            <span>{speaking ? "Stop briefing" : "Listen to my briefing"}<span className="block text-xs font-400 opacity-80">Reminders + today's suggestions, read aloud</span></span>
          </button>
          <button onClick={() => setVoicePanel((v) => !v)} title="Voice settings" data-testid="msp-voice-settings-btn"
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white/20 text-white hover:bg-white/35 transition-colors">
            <Settings2 className="w-4 h-4" />
          </button>
          {voicePanel && <VoicePicker onClose={() => setVoicePanel(false)} />}
        </div>
      </div>

      <section className="mt-5">
        <div className="flex items-center gap-3">
          <h2 className="font-heading font-800 text-lg">Reminders</h2>
          <Link to="/remind-me" className="text-xs font-700 text-blue-700 hover:underline ml-auto" data-testid="msp-reminders-all">View all</Link>
        </div>
        {!reminders ? <p className="text-sm text-zinc-500 mt-2">Loading…</p> : reminders.length === 0 ? (
          <p className="text-sm text-zinc-500 mt-2 border border-dashed border-zinc-300 rounded-xl py-4 text-center" data-testid="msp-reminders-empty">Nothing due today.</p>
        ) : (
          <ul className="mt-2 space-y-1.5" data-testid="msp-reminders-list">
            {reminders.slice(0, 5).map((r) => (
              <li key={r.id} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-2.5">
                <Bell className="w-4 h-4 text-rose-600 shrink-0" />
                <span className="text-sm font-600">{r.title}</span>
                <span className="ml-auto text-xs text-zinc-400">{r.remind_at ? new Date(r.remind_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                <input type="checkbox" checked={false} title="Mark done" data-testid={`msp-reminder-done-${r.id}`}
                  onChange={() => markDone(r)} className="w-5 h-5 accent-blue-700 cursor-pointer" />
              </li>
            ))}
          </ul>
        )}
      </section>

      <PantryLowStock />
      <ExpiryMealIdeas />

      <section className="mt-5">
        <h2 className="font-heading font-800 text-lg">Quick actions</h2>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {QUICK.map((q) => (
            <Link key={q.label} to={q.to} data-testid={`msp-quick-${q.label.toLowerCase().replace(/\s/g, "-")}`}
              className="flex flex-col items-center gap-1.5 bg-white border border-zinc-200 rounded-2xl py-3.5 px-1 hover:border-blue-400 hover:-translate-y-0.5 transition-[transform,border-color]">
              <span className={`inline-flex p-2.5 rounded-full text-white ${q.tone}`}><q.icon className="w-4 h-4" /></span>
              <span className="text-[11px] sm:text-sm font-600 text-center leading-tight">{q.label}</span>
            </Link>
          ))}
        </div>
      </section>

      <button onClick={() => window.dispatchEvent(new Event("tp:ask-open"))} data-testid="msp-ask-bar"
        className="mt-5 w-full flex items-center gap-2.5 bg-white border border-zinc-300 rounded-full px-4 py-3 text-sm text-zinc-400 hover:border-blue-500 transition-colors text-left">
        <Mic className="w-4 h-4 text-blue-700 shrink-0" /> Ask or tell SocialPulse…
      </button>

      <section className="mt-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-heading font-800 text-lg">For you today</h2>
          <span className="text-xs text-zinc-400">A small selection — never an endless feed</span>
        </div>
        <div className="mt-2"><InterestChips /></div>
        <div className="mt-3"><FeedGrid module="today" emptyText="No suggestions today — enable interests above to see more." /></div>
      </section>
    </div>
  );
}

/* ---------------- Pantry low-stock cards (Today) ----------------
   Shows pantry items that are running low (qty <= 1) with a one-tap
   "Add to list" that drops them on the shared shopping list. */
function PantryLowStock() {
  const [items, setItems] = useState([]);
  const [added, setAdded] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/life/pantry").catch(() => ({ data: {} })),
      api.get("/life/shopping").catch(() => ({ data: {} })),
    ]).then(([p, s]) => {
      const low = (p.data.items || []).filter((it) => (it.qty ?? 0) <= 1);
      const onList = new Set((s.data.items || []).filter((x) => !x.done).map((x) => (x.name || "").toLowerCase()));
      setItems(low);
      setAdded(Object.fromEntries(low.filter((it) => onList.has((it.name || "").toLowerCase())).map((it) => [it.id, true])));
    });
  }, []);

  const addToList = async (it) => {
    try {
      await api.post(`/life/pantry/${it.id}/to-shopping`);
      setAdded((m) => ({ ...m, [it.id]: true }));
      toast.success(`"${it.name}" added to the shopping list`);
    } catch { toast.error("Could not add — try again"); }
  };

  if (items.length === 0) return null;
  return (
    <section className="mt-5" data-testid="msp-low-stock">
      <div className="flex items-center gap-3">
        <h2 className="font-heading font-800 text-lg">Running low at home</h2>
        <Link to="/my-social-pulse/pantry" className="text-xs font-700 text-blue-700 hover:underline ml-auto" data-testid="msp-low-stock-all">Open pantry</Link>
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.slice(0, 4).map((it) => (
          <div key={it.id} className="bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 flex flex-col gap-1.5" data-testid={`msp-low-stock-${it.id}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <Package className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-sm font-700 truncate">{it.name}</span>
            </div>
            <span className="text-[11px] text-amber-700">{(it.qty ?? 0) === 0 ? "Out of stock" : `Only ${it.qty}${it.unit ? ` ${it.unit}` : ""} left`} · {it.location || "pantry"}</span>
            <button onClick={() => addToList(it)} disabled={added[it.id]} data-testid={`msp-low-stock-add-${it.id}`}
              className={`mt-auto text-xs font-700 rounded-full px-2.5 py-1.5 transition-colors ${added[it.id] ? "bg-emerald-100 text-emerald-700" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
              {added[it.id] ? "On the list ✓" : "+ Add to list"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Expiry meal ideas (Today) ----------------
   Pantry items expiring within 7 days + a one-tap LLM recipe using them. */
function ExpiryMealIdeas() {
  const [items, setItems] = useState([]);
  const [recipe, setRecipe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [listBusy, setListBusy] = useState(false);
  const [listAdded, setListAdded] = useState(false);

  const addMissing = async () => {
    // "missing" = ingredients NOT marked * (the * ones are the expiring pantry items)
    const missing = recipe.ingredients.filter((i) => !i.trim().startsWith("*"));
    if (missing.length === 0) { toast.success("You already have everything at home!"); setListAdded(true); return; }
    setListBusy(true);
    try {
      const r = await api.post("/life/meal-ideas/to-shopping", { ingredients: missing });
      const { added, skipped } = r.data;
      toast.success(added.length === 0 ? "Everything is already covered at home or on the list"
        : `${added.length} added to the shopping list${skipped.length ? ` · ${skipped.length} already covered` : ""}`);
      setListAdded(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add — try again"); }
    setListBusy(false);
  };

  useEffect(() => {
    api.get("/life/expiring").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try { setRecipe((await api.post("/life/meal-ideas")).data.recipe); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not fetch a recipe — try again"); }
    setBusy(false);
  };

  if (items.length === 0) return null;
  return (
    <section className="mt-5" data-testid="msp-meal-ideas">
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 font-heading font-800"><ChefHat className="w-5 h-5 text-emerald-700" /> Use it before it expires</div>
        <p className="mt-1 text-sm text-zinc-600" data-testid="msp-meal-expiring">
          Expiring this week: {items.slice(0, 6).map((it) => `${it.name} (${it.expiry_date})`).join(", ")}{items.length > 6 ? "…" : ""}
        </p>
        {!recipe ? (
          <button onClick={generate} disabled={busy} data-testid="msp-meal-generate-btn"
            className="mt-3 inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 text-sm font-700 rounded-full hover:bg-emerald-700 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChefHat className="w-4 h-4" />}
            {busy ? "Cooking up an idea…" : "Get a quick recipe idea"}
          </button>
        ) : (
          <div className="mt-3 bg-white border border-emerald-200 rounded-xl p-4" data-testid="msp-meal-recipe">
            <div className="font-heading font-800 text-base">{recipe.title}
              <span className="ml-2 text-xs font-600 text-emerald-700">~{recipe.time_minutes} min</span>
            </div>
            <p className="mt-1.5 text-xs text-zinc-500">Ingredients (* = expiring): {recipe.ingredients.join(" · ")}</p>
            <ol className="mt-2 space-y-1 text-sm text-zinc-700 list-decimal list-inside">
              {recipe.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            {recipe.tip && <p className="mt-2 text-xs text-emerald-800 bg-emerald-50 rounded-lg px-2.5 py-1.5">Tip: {recipe.tip}</p>}
            <button onClick={addMissing} disabled={listBusy || listAdded} data-testid="msp-meal-to-list-btn"
              className={`mt-3 inline-flex items-center gap-1.5 text-xs font-700 rounded-full px-3.5 py-2 transition-colors ${listAdded ? "bg-emerald-100 text-emerald-700" : "bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"}`}>
              {listBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
              {listAdded ? "Missing ingredients on the list ✓" : "Add missing ingredients to shopping list"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- Voice picker (per-device, saved locally) ---------------- */
function VoicePicker({ onClose }) {
  const [voices, setVoices] = useState([]);
  const [prefs, setPrefs] = useState(getTtsPrefs());

  useEffect(() => {
    const load = () => setVoices(window.speechSynthesis?.getVoices() || []);
    load();
    window.speechSynthesis?.addEventListener?.("voiceschanged", load);
    return () => window.speechSynthesis?.removeEventListener?.("voiceschanged", load);
  }, []);

  const save = (next) => {
    setPrefs(next);
    try { localStorage.setItem("tp_tts", JSON.stringify(next)); } catch { /* private mode */ }
  };
  const test = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance("Good morning! This is how your briefing will sound.");
    applyTtsPrefs(u);
    synth.speak(u);
  };

  return (
    <div className="absolute right-0 top-11 z-30 w-72 bg-white border border-zinc-200 rounded-2xl shadow-xl p-4" data-testid="msp-voice-panel">
      <div className="text-sm font-heading font-800">Briefing voice</div>
      <select value={prefs.voiceURI || ""} onChange={(e) => save({ ...prefs, voiceURI: e.target.value })}
        data-testid="msp-voice-select"
        className="notranslate mt-2 w-full border border-zinc-300 rounded-lg px-2 py-2 text-sm bg-white">
        <option value="">Device default</option>
        {voices.map((v) => <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>)}
      </select>
      <div className="mt-3 text-xs font-700 text-zinc-600 flex justify-between">
        <span>Speed</span><span className="font-mono">{(prefs.rate || 0.95).toFixed(2)}×</span>
      </div>
      <input type="range" min="0.6" max="1.4" step="0.05" value={prefs.rate || 0.95}
        onChange={(e) => save({ ...prefs, rate: Number(e.target.value) })}
        data-testid="msp-voice-rate" className="w-full accent-blue-700" />
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={test} data-testid="msp-voice-test" className="text-xs font-700 text-blue-700 border border-blue-200 bg-blue-50 rounded-lg px-3 py-1.5 hover:bg-blue-100">Test voice</button>
        <button onClick={onClose} data-testid="msp-voice-close" className="text-xs font-700 border border-zinc-300 rounded-lg px-3 py-1.5">Done</button>
      </div>
      <p className="mt-2 text-[10px] text-zinc-400">Saved on this device — voices vary by phone and browser.</p>
    </div>
  );
}

/* ---------------- My Life: colorful round-icon grid (mockup style) ---------------- */
function MyLifeTab() {
  return (
    <div className="mt-4" data-testid="msp-life">
      <div className="grid grid-cols-3 gap-3 sm:gap-4" data-testid="msp-tiles">
        {TILES.map((t) => (
          <Link key={t.title} to={t.to} data-testid={`msp-tile-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="group flex flex-col items-center gap-2.5 bg-white border border-zinc-200 rounded-2xl px-2 py-5 text-center hover:-translate-y-0.5 hover:shadow-lg hover:border-zinc-300 transition-[transform,box-shadow,border-color]">
            <span className={`inline-flex p-4 rounded-full text-white ${t.tone} group-hover:scale-105 transition-transform`}><t.icon className="w-6 h-6" /></span>
            <span className="font-heading font-800 text-sm leading-tight">{t.title}</span>
          </Link>
        ))}
      </div>
      <p className="mt-4 text-xs text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-xl px-4 py-2.5 flex items-center gap-1.5 justify-center">🔒 Private unless you choose to share</p>
    </div>
  );
}

/* ---------------- Family ---------------- */
function FamilyTab() {
  const CARDS = [
    { icon: TreePine, title: "Our Family Tree", text: "Open your tree on IndianFamilyTree.com. Explicit account linking arrives in a later phase — names are never matched automatically.", href: "https://indianfamilytree.com", external: true, testid: "family-tree" },
    { icon: BookOpen, title: "Family Memories", text: "Photos, stories and moments from your memoirs.", to: "/memoirs", testid: "family-memories" },
    { icon: Flower2, title: "Memorials & Tributes", text: "Coming soon — created only through verified invitations, with sensitive wording and no advertising.", soon: true, testid: "family-memorials" },
    { icon: CalendarHeart, title: "Birthdays & Anniversaries", text: "Reminders are opt-in per person and easy to pause. Set them up in Reminders.", to: "/remind-me", testid: "family-dates" },
  ];
  return (
    <div className="mt-4" data-testid="msp-family">
      <FamilyCircle />
      <p className="mt-4 text-sm text-zinc-600 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">Family spaces use explicit invitations. Sharing a shopping list never opens your photos, memorials or relatives' details.</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {CARDS.map((c) => {
          const inner = (
            <>
              <span className="inline-flex p-3 rounded-full bg-violet-100 text-violet-700 shrink-0"><c.icon className="w-6 h-6" /></span>
              <div className="min-w-0">
                <div className="font-heading font-800 text-base flex items-center gap-2">{c.title}
                  {c.soon && <span className="text-[10px] font-800 uppercase bg-zinc-100 text-zinc-500 border border-zinc-300 rounded px-1.5 py-0.5">Soon</span>}
                  {c.external && <ExternalLink className="w-3.5 h-3.5 text-zinc-400" />}
                </div>
                <div className="text-sm text-zinc-500 mt-0.5">{c.text}</div>
              </div>
            </>
          );
          const cls = "flex items-start gap-4 bg-white border border-zinc-200 rounded-2xl p-5 hover:border-violet-300 transition-colors";
          if (c.soon) return <div key={c.title} className={`${cls} opacity-70`} data-testid={c.testid}>{inner}</div>;
          if (c.external) return <a key={c.title} href={c.href} target="_blank" rel="noreferrer" className={cls} data-testid={c.testid}>{inner}</a>;
          return <Link key={c.title} to={c.to} className={cls} data-testid={c.testid}>{inner}</Link>;
        })}
      </div>
    </div>
  );
}

/* Verified family invitations (explicit account linking — never name matching) */
function FamilyCircle() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", relation: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/family").then((r) => setData(r.data)).catch(() => setData({ members: [] }));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/family/invite", form);
      toast.success(`Invitation emailed to ${form.email}`);
      setForm({ name: "", email: "", relation: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the invitation"); }
    setBusy(false);
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the family space? Their access ends immediately.`)) return;
    try { await api.delete(`/family/members/${m.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!data) return <p className="mt-4 text-sm text-zinc-500">Loading family space…</p>;
  const isOwner = data.role === "owner" || !data.circle;
  return (
    <div className="mt-4 bg-white border border-zinc-200 rounded-2xl p-4" data-testid="family-circle">
      <div className="font-heading font-800 flex items-center gap-2"><Users className="w-4 h-4 text-violet-700" /> Your family space
        {data.role === "member" && <span className="text-[10px] font-800 uppercase bg-violet-50 text-violet-700 border border-violet-300 rounded px-1.5 py-0.5">Member of {data.owner?.name}'s space</span>}
      </div>
      {isOwner && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Relative's name" data-testid="family-invite-name"
            className="flex-1 min-w-[140px] border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
          <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="their@email.com" type="email" data-testid="family-invite-email"
            className="flex-1 min-w-[170px] border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
          <input value={form.relation} onChange={(e) => setForm((f) => ({ ...f, relation: e.target.value }))}
            placeholder="Relation (optional)" data-testid="family-invite-relation"
            className="w-36 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
          <button onClick={invite} disabled={busy} data-testid="family-invite-btn"
            className="bg-violet-700 text-white px-5 py-2.5 rounded-lg font-700 text-sm hover:bg-violet-600 disabled:opacity-50">
            {busy ? "Sending…" : "Send invitation"}
          </button>
        </div>
      )}
      <p className="text-[11px] text-zinc-400 mt-2">They receive an email and join with their own account — access can be revoked any time and ends immediately.</p>
      {isOwner && data.circle && (
        <div className="mt-3 flex items-center gap-2 text-sm border-t border-zinc-100 pt-3" data-testid="family-digest-row">
          <CalendarHeart className="w-4 h-4 text-violet-700 shrink-0" />
          <span className="font-600">Weekly digest email:</span>
          <select value={data.digest_day || "sunday"} data-testid="family-digest-day"
            onChange={async (e) => {
              const v = e.target.value;
              try {
                await api.put("/family/digest", { digest_day: v });
                toast.success(v === "off" ? "Weekly digest turned off" : `Digest will arrive on ${v[0].toUpperCase()}${v.slice(1)}s`);
                load();
              } catch (er) { toast.error(er.response?.data?.detail || "Could not save"); }
            }}
            className="border border-zinc-300 rounded-lg px-2 py-1.5 text-sm bg-white">
            {["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].map((d) => (
              <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>
            ))}
            <option value="off">Off — no digest</option>
          </select>
        </div>
      )}
      {data.members.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="family-members-list">
          {data.members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2" data-testid={`family-member-${m.id}`}>
              <span className="text-sm font-600">{m.name}</span>
              {m.relation && <span className="text-xs text-zinc-500">{m.relation}</span>}
              <span className={`text-[10px] font-800 uppercase rounded px-1.5 py-0.5 border ${m.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
                {m.status === "active" ? "Joined" : "Invited"}
              </span>
              <button onClick={() => remove(m)} data-testid={`family-remove-${m.id}`}
                className="ml-auto text-zinc-300 hover:text-rose-600 text-xs font-700">Remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------------- News & Listening (browser SpeechSynthesis) ---------------- */
function DailyNews() {
  const [items, setItems] = useState(null);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/news", { params: { category: "travel", limit: 8 } })
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    return () => window.speechSynthesis?.cancel();
  }, []);

  const speak = (text, id) => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    if (playing === id) { setPlaying(null); return; }
    const u = new SpeechSynthesisUtterance(text);
    applyTtsPrefs(u);
    u.onend = () => setPlaying(null);
    setPlaying(id);
    synth.speak(u);
  };

  if (!items) return <p className="mt-6 text-sm text-zinc-500">Loading headlines…</p>;
  return (
    <section className="mt-4 bg-white border border-zinc-200 rounded-2xl" data-testid="msp-news">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-zinc-100">
        <Newspaper className="w-5 h-5 text-blue-700" />
        <h2 className="font-heading font-800 text-lg">Daily news</h2>
        {items.length > 0 && (
          <button onClick={() => speak(items.map((n, i) => `Story ${i + 1}. ${n.title}.`).join(" "), "all")}
            data-testid="msp-news-listen-all"
            className={`ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-700 transition-colors ${playing === "all" ? "bg-rose-600 text-white" : "bg-blue-700 text-white hover:bg-blue-800"}`}>
            {playing === "all" ? <><Square className="w-3.5 h-3.5" /> Stop</> : <><Volume2 className="w-4 h-4" /> Listen to headlines</>}
          </button>
        )}
      </div>
      <p className="px-5 pt-3 text-xs text-zinc-500">Headlines and short summaries are read aloud with your device's built-in voice. Full articles open at the original publisher.</p>
      {items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-zinc-500">No headlines right now — check back soon.</p>
      ) : (
        <ul className="divide-y divide-zinc-100 mt-2">
          {items.map((n, i) => (
            <li key={i} className="flex items-start gap-3 px-5 py-3.5" data-testid={`msp-news-item-${i}`}>
              <button onClick={() => speak(`${n.title}. ${n.summary || ""}`, i)} title={playing === i ? "Stop" : "Listen"}
                data-testid={`msp-news-listen-${i}`}
                className={`shrink-0 mt-0.5 p-2 rounded-full border transition-colors ${playing === i ? "bg-rose-600 border-rose-600 text-white" : "border-zinc-300 text-blue-700 hover:border-blue-500"}`}>
                {playing === i ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <div className="min-w-0">
                <a href={n.link} target="_blank" rel="noreferrer" className="text-sm font-700 text-zinc-800 hover:text-blue-700 transition-colors">{n.title}</a>
                <div className="text-xs text-zinc-500 mt-0.5">{n.source || ""}{n.published ? ` · ${n.published}` : ""}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="px-5 py-3 border-t border-zinc-100 text-xs"><Link to="/travel-news" className="text-blue-700 font-700 hover:underline">All travel news →</Link></div>
    </section>
  );
}
